// JSON-LD Structured Data Templates for SEO

export const createOrganizationSchema = (language: 'ar' | 'en') => ({
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "name": language === 'ar' ? "هُدًى للمتقين - منصة تعليم القرآن والعربية" : "Hudan Lil Muttaqeen - Quran & Arabic Learning Platform",
  "alternateName": language === 'ar' ? "Hudan Lil Muttaqeen" : "هُدًى للمتقين",
  "url": "https://hudanlilmuttaqeen.com",
  "logo": "https://hudanlilmuttaqeen.com/favicon.svg",
  "description": language === 'ar'
    ? "منصة تعليمية متخصصة في تعليم القرآن الكريم واللغة العربية عبر الإنترنت مع معلمين مؤهلين من جميع أنحاء العالم"
    : "Online learning platform specializing in Quran and Arabic language education with qualified teachers from around the world",
  "inLanguage": language === 'ar' ? "ar" : "en",
  "sameAs": [
    "https://facebook.com/hudanplatform",
    "https://twitter.com/hudanplatform",
    "https://instagram.com/hudanplatform",
    "https://youtube.com/hudanplatform"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": ["Arabic", "English"]
  }
});

export const createWebsiteSchema = (language: 'ar' | 'en') => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": language === 'ar' ? "هُدًى للمتقين" : "Hudan Lil Muttaqeen",
  "url": "https://hudanlilmuttaqeen.com",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://hudanlilmuttaqeen.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  },
  "inLanguage": language === 'ar' ? "ar" : "en"
});

export const createCourseSchema = (course: {
  name: string;
  description: string;
  provider: string;
  price?: number;
  currency?: string;
  language: 'ar' | 'en';
}) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  "name": course.name,
  "description": course.description,
  "provider": {
    "@type": "Organization",
    "name": course.provider,
    "sameAs": "https://hudanlilmuttaqeen.com"
  },
  "offers": course.price ? {
    "@type": "Offer",
    "price": course.price,
    "priceCurrency": course.currency || "USD",
    "availability": "https://schema.org/InStock"
  } : undefined,
  "inLanguage": course.language === 'ar' ? "ar" : "en",
  "courseMode": "online",
  "educationalLevel": "beginner to advanced"
});

export const createBreadcrumbSchema = (
  items: { name: string; url: string }[],
  inLanguage?: string
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  ...(inLanguage ? { "inLanguage": inLanguage } : {}),
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const createFAQSchema = (
  faqs: { question: string; answer: string }[],
  inLanguage?: string
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  ...(inLanguage ? { "inLanguage": inLanguage } : {}),
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const createPersonSchema = (teacher: {
  name: string;
  description: string;
  image?: string;
  expertise: string[];
  url?: string;
  alternateName?: string;
  inLanguage?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "name": teacher.name,
  ...(teacher.alternateName ? { "alternateName": teacher.alternateName } : {}),
  "description": teacher.description,
  "image": teacher.image,
  "url": teacher.url,
  "jobTitle": "Quran & Arabic Teacher",
  "knowsAbout": teacher.expertise,
  ...(teacher.inLanguage ? { "inLanguage": teacher.inLanguage } : {}),
  "worksFor": {
    "@type": "Organization",
    "name": "Hudan Lil Muttaqeen"
  }
});

export const createLocalBusinessSchema = (opts: {
  language: 'ar' | 'en';
  telephone?: string;
  email?: string;
  streetAddress?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  openingHours?: Array<{
    dayOfWeek: string[];
    opens: string;
    closes: string;
  }>;
  sameAs?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "name": opts.language === 'ar'
    ? "هُدًى للمتقين - منصة تعليم القرآن والعربية"
    : "Hudan Lil Muttaqeen - Quran & Arabic Learning Platform",
  "url": "https://hudanlilmuttaqeen.com",
  "logo": "https://hudanlilmuttaqeen.com/favicon.svg",
  "inLanguage": opts.language === 'ar' ? "ar" : "en",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": opts.language === 'ar' ? "الشارقة" : "Sharjah",
    "addressRegion": opts.language === 'ar' ? "إمارة الشارقة" : "Sharjah Emirate",
    "addressCountry": "AE",
    ...(opts.streetAddress ? { "streetAddress": opts.streetAddress } : {}),
    ...(opts.postalCode ? { "postalCode": opts.postalCode } : {})
  },
  ...(opts.latitude != null && opts.longitude != null ? {
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": opts.latitude,
      "longitude": opts.longitude
    }
  } : {}),
  ...(opts.telephone ? { "telephone": opts.telephone } : {}),
  ...(opts.email ? { "email": opts.email } : {}),
  ...(opts.openingHours?.length ? {
    "openingHoursSpecification": opts.openingHours.map(h => ({
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": h.dayOfWeek,
      "opens": h.opens,
      "closes": h.closes
    }))
  } : {}),
  "sameAs": opts.sameAs ?? [
    "https://facebook.com/hudanplatform",
    "https://twitter.com/hudanplatform",
    "https://instagram.com/hudanplatform",
    "https://youtube.com/hudanplatform"
  ]
});
